<!--赖传峰-->
<!--2022/3/30-->
<script lang="ts" setup>
import {
  computed,
  defineAsyncComponent,
  defineI18nRoute,
  definePageMeta,
  navigateTo,
  onMounted,
  provide,
  ref,
  toRefs,
  useCookie,
  useHead,
  useI18n,
  useNuxtApp,
  useRequestHeaders,
  useRoute,
  useRouter,
  useSeoMeta,
  watchEffect,
} from '#imports'
import ogNoneThumbnailImage from '@/assets/images/share/og-none-thumbnail-image.png'
import { isVideoOrImage } from '@/components/share/fileExplorer.script'
import FileSpaceNotEnough from '@/components/share/FileSpaceNotEnough.vue'
import { isFolder, shareTrackParams } from '@/components/share/share.script'
import useDomain from '@/composables/useDomain'
import useParentId from '@/composables/useParentId'
import { useShareAction } from '@/composables/useShareAction'
import useShareId from '@/composables/useShareId'
import { useLayout } from '@/store/layout'
import { RestoreResultStatus, type ShareState, useShare } from '@/store/share'
import { getOrderTypeFromStorage, isNaturalSort, naturalSort, paramOrder } from '@/store/share.script'
import $track from '@/track/webShare'
import { DriveKind, FileOrder, InjectionKey, RouteQueryKey, ShareStatus, StorageKey } from '@/types/enum'
import clearQueryByParams from '@/utils/clearQueryByParams'
import { hydrateOnVisible } from 'vue'
import { type GetShareQuery, ThumbnailSize } from '~/api'
import { fetchShareResData, getBatchFileInfo, getShareFileList } from '~/api/ssr'
import FileExplorer from '~/components/share/FileExplorer.vue'
import useDisplayMode from '~/composables/useDisplayMode'
import { useSharePageTitle } from '~/composables/usePageTitle'
import useShareFiles from '~/composables/useShareFiles'
import { ipBlock } from '~/middleware/ipBlock.global'
import { BOT_REGEX } from '~/types/constants'
import isMobileInServerAndClient from '~/utils/isMobileInServerAndClient'

const AsyncRestoreResult = defineAsyncComponent({
  loader: () => import('@/components/share/RestoreResult.vue'),
  hydrate: hydrateOnVisible(),
})
const AsyncNeedPasscode = defineAsyncComponent({
  loader: () => import('@/components/share/NeedPasscode.vue'),
  hydrate: hydrateOnVisible(),
})
const AsyncShareNotAvailable = defineAsyncComponent({
  loader: () => import('@/components/share/ShareNotAvailable.vue'),
  hydrate: hydrateOnVisible(),
})
const AsyncActivityTopBar = defineAsyncComponent(() => import('@/components/activity/ActivityTopBar.vue'))

// 是否显示文件的缩略图与文件名，如果是搜索引擎或者爬虫，默认值为 true，否则为 false
const isRobot = ref(BOT_REGEX.test(useRequestHeaders(['user-agent'])['user-agent'] || ''))

const shareStore = useShare()
const { $getPasscodeToken, $clearPasscodeToken, $setPasscodeToken } = useNuxtApp()
const { t } = useI18n()

const parentId = useParentId()
const shareId = useShareId()
const router = useRouter()
const route = useRoute()

const { isMobile } = toRefs(useLayout())
const passCode = route.query[RouteQueryKey.SHARE_PASSCODE] as string

const orderType = await getOrderTypeFromStorage(true)
const { pinFoldersTop } = useDisplayMode()

// 构建分享API请求参数
const shareQuery: GetShareQuery = {
  share_id: shareId.value,
  pass_code: passCode,
  pass_code_token: $getPasscodeToken().value,
  thumbnail_size: isMobileInServerAndClient() ? ThumbnailSize.SIZE_SMALL : ThumbnailSize.SIZE_LARGE,
  limit: 1e2,
  folders_first: pinFoldersTop.value,
  order: paramOrder(orderType),
}

const shareResData = await fetchShareResData(shareQuery)

if (import.meta.server && shareResData) {
  shareResData.pass_code_token && $setPasscodeToken(shareResData.pass_code_token)
  const code = passCode || useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`).value
  code && (useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`, { maxAge: 7 * 24 * 60 * 60 }).value = code)
  const queryAction = useShareAction()

  if (queryAction) shareResData.params = queryAction

  const { params, files } = shareResData
  const canEnterSubDir = params && (params.auto_enter_subdir === 'true' || params.auto_play === 'true')
  if (isRobot.value) {
    // 搜索引擎爬虫不进行自动跳转
  } else if (canEnterSubDir && files.length === 1 && isFolder(files[0].kind) && !parentId.value) {
    navigateTo({
      path: `/s/${shareId.value}/${files[0].id}`,
      query: route.query,
    })
  } else if (queryAction) {
    shareStore.urlQuery = queryAction
  }
}

let shareDetailResData = null

// 兼容旧版本浏览器
if (!Promise.allSettled) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Promise.allSettled = function (promises: Promise<any>[]) {
    return Promise.all(
      promises.map(async promise => {
        try {
          const value = await promise
          return { status: 'fulfilled' as const, value }
        } catch (reason) {
          return { status: 'rejected' as const, reason }
        }
      }),
    )
  }
}

const parentIds = computed(() => {
  const arr = route.params.parent_ids
  if (Array.isArray(arr)) {
    return arr
  }
  return [arr]
})
// 获取当前分享文件列表 / 目录名称
if (import.meta.server && parentIds.value.length > 0 && shareResData?.share_status === ShareStatus.OK) {
  try {
    const p1 = getBatchFileInfo({
      share_id: shareId.value,
      file_ids: parentIds.value,
      pass_code_token: shareResData?.pass_code_token || '',
    })

    // 当前位于子目录场景，获取当前文件下分享文件列表
    const p2 = getShareFileList({
      ...shareQuery,
      parent_id: parentId.value, // 子文件夹ID
      pass_code_token: shareResData?.pass_code_token, // 使用之前获取的密码令牌
    })

    const [res1, res2] = await Promise.allSettled([p1, p2])
    const normalRes = {
      share_status: ShareStatus.OK,
      files: [],
      next_page_token: '',
    }
    if (res1.status === 'fulfilled') {
      const data = res1?.value?.data?.value || normalRes
      shareStore.dirs = data?.files?.map(el => ({
        id: el.id,
        name: el.name,
      }))
    }
    if (res2.status === 'fulfilled') {
      shareDetailResData = res2.value?.data?.value || normalRes
    }
  } catch (error) {
    console.error(error)
  }
}

// 计算属性：获取当前分享数据
const share = computed<Share>(() => {
  if (import.meta.client) return shareStore.data

  if (shareResData) {
    if (isNaturalSort(orderType) && shareResData.files) {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      shareResData.files = naturalSort(shareResData.files, orderType as FileOrder.NAME_ASC)
    }
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    shareStore.data = shareResData
    return shareResData
  }

  return shareStore.data
})

const isNeedPasscodeClass = computed(() => !isMobile.value && share.value.share_status === ShareStatus.PASS_CODE_EMPTY)

const shareDetail = computed<ShareState['detail'][string]>(() => {
  if (!parentId.value) return null
  if (import.meta.client) return shareStore.detail[parentId.value]

  if (isNaturalSort(orderType) && shareDetailResData?.files) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    shareDetailResData.files = naturalSort(shareDetailResData.files, orderType as FileOrder.NAME_ASC)
  }
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  shareStore.detail[parentId.value] = shareDetailResData
  return shareDetailResData
})

provide<ShareRef>(InjectionKey.SHARE, share)
provide<ShareDetailRef>(InjectionKey.SHARE_DETAIL, shareDetail)

const files4Render = useShareFiles({ share, shareDetail })
const isSingleShareFile = computed(
  () =>
    !parentId.value &&
    files4Render.value.length === 1 &&
    files4Render.value[0].kind === DriveKind.FILE &&
    (isMobile.value ? true : isVideoOrImage(files4Render.value[0].file_extension, files4Render.value[0].mime_type)),
)

const generateHtmlMetaContent = (
  dirsList: string[],
  nameList: string[],
  titlePostfix: string,
  nameLength: number,
  maxLength: number,
) => {
  const dirStr = dirsList.join(' ')
  const nameStr = nameList.slice(0, nameLength).join(' ')
  let finalStr = dirStr ? `${dirStr} ${nameStr}` : nameStr
  finalStr = finalStr.replace(/[/\\|?*:"<>]/g, '-')

  const prefixLength = maxLength - titlePostfix.length

  if (prefixLength > 0 && finalStr.length > prefixLength) {
    return `${finalStr.slice(0, prefixLength)}...`
  } else if (prefixLength > 0) {
    return finalStr
  } else {
    return ''
  }
}

const parentsFolderNames = computed(() => {
  return shareStore.dirs?.map(el => el.name)
})

// 生成页面元数据
const htmlMetaData = computed<{
  ogTitle: string
  ogDescription?: string
  ogImage?: string
  pageTitle: string
  pageDescription: string
}>(() => {
  let titlePostfix = t('share-author', [share.value.user_info?.nickname])
  titlePostfix = useSharePageTitle(titlePostfix).value

  if (share.value.share_status === ShareStatus.OK) {
    const folderList = [...files4Render.value].filter(el => el.kind === DriveKind.FOLDER).slice(0, 5)
    const fileList = [...files4Render.value].filter(el => el.kind !== DriveKind.FOLDER)
    const dirsList = parentsFolderNames.value || []

    fileList.sort((aFile, bFile) => {
      const aSize = Number(aFile.size) || 0
      const bSize = Number(bFile.size) || 0
      return bSize - aSize
    })
    folderList.splice(5, 0, ...fileList)

    const mergeList = folderList.map(el => el.name)
    const mergedNameFor10 = mergeList.slice(0, 10).join(' ')
    const ogTitle = t('share-og-title', [mergedNameFor10])

    let pageTitle = ''

    if (parentId.value) {
      const nameList = shareStore.dirs?.filter(el => el.id === parentIds.value[0])?.map(el => el.name) || []
      pageTitle = generateHtmlMetaContent([], nameList, titlePostfix, 4, 60)
    } else {
      pageTitle = generateHtmlMetaContent([], mergeList, titlePostfix, 4, 60)
    }

    let pageDescription = generateHtmlMetaContent(dirsList, mergeList, t('seo-page-share-description'), 10, 160) // 最多160个字符

    pageTitle = `${pageTitle} ${titlePostfix}`
    pageDescription = t('seo-page-share-description', [pageDescription])

    return {
      ogTitle,
      ogDescription: titlePostfix,
      ogImage: fileList.map(el => el.thumbnail_link).find(el => el) || ogNoneThumbnailImage,
      pageTitle,
      pageDescription,
    }
  }

  return {
    ogTitle: titlePostfix,
    ogDescription: titlePostfix,
    ogImage: ogNoneThumbnailImage,
    pageTitle: titlePostfix,
    pageDescription: titlePostfix,
  }
})

// 设置SEO元数据
useSeoMeta({
  title: htmlMetaData.value.pageTitle,
  description: htmlMetaData.value.pageDescription,
  ogType: 'video.movie',
  ogDescription: htmlMetaData.value.pageDescription,
  ogTitle: htmlMetaData.value.ogTitle,
  ogImage: htmlMetaData.value.ogImage ? { url: htmlMetaData.value.ogImage } : undefined,
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: `https://${useDomain().value}${route.path}`,
    },
  ],
})

const hasOperationStatus = computed(() =>
  [ShareStatus.OK, ShareStatus.PASS_CODE_EMPTY, ShareStatus.PASS_CODE_ERROR].includes(share.value.share_status),
)

onMounted(() => {
  if (!share.value || share.value.share_status !== ShareStatus.OK) {
    $clearPasscodeToken()
    useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`, { maxAge: 0 }).value = null
  }
  if (shareStore.data.share_status === ShareStatus.OK && parentId.value && !shareStore.detail[parentId.value]) {
    router.replace({
      params: {
        share_id: shareId.value,
        parent_ids: undefined,
      },
    })
  }

  clearQueryByParams(['act'])
})

// 页面访问统计跟踪
watchEffect(() => {
  if (import.meta.server) return
  if (!useCookie(StorageKey.ACCESS_TO_VISIT).value) return
  switch (share.value.share_status) {
    case ShareStatus.OK:
      return $track.web_share_page_show(shareTrackParams())
    case ShareStatus.PASS_CODE_EMPTY:
    case ShareStatus.PASS_CODE_ERROR:
      return $track.web_share_password_page_show()
    default:
      $track.web_share_abnormal_page_show({ error: share.value.share_status })
  }
})

onMounted(() => {
  ipBlock({ ignoreCache: true }).then(val => (isRobot.value = val === undefined))
})

definePageMeta({
  layout: 'drive',
  middleware: 'share',
})

// 禁用i18n路由自动生成
false && /*#__PURE__*/ defineI18nRoute(false)
</script>

<template>
  <div
    :class="{ 'need-passcode': isNeedPasscodeClass, 'single-file': isSingleShareFile }"
    class="share"
  >
    <div class="share-container">
      <AsyncRestoreResult v-if="isMobile && shareStore.restoreResult.status !== RestoreResultStatus.UNKNOWN" />
      <template v-else-if="hasOperationStatus">
        <template v-if="share.share_status === ShareStatus.OK">
          <ClientOnly>
            <AsyncActivityTopBar scene="share_page" />
          </ClientOnly>
          <FileExplorer :show-file-info="isRobot" />
        </template>
        <AsyncNeedPasscode v-else />
      </template>
      <AsyncShareNotAvailable
        v-else
        :status="share.share_status"
        :status-text="share.share_status_text"
      />
      <ClientOnly>
        <FileSpaceNotEnough />
      </ClientOnly>
    </div>
  </div>
</template>

<style lang="less">
@import '@/assets/styles/util.less';

.share {
  height: 100%;

  &-container {
    display: flex;
    flex-direction: column;
    height: 100%;

    .activity-top-bar {
      flex: 0 0 auto;
    }

    .file-explorer:not(.single-file) {
      flex: 1 1 auto;
      height: 0 !important;
    }
  }

  @media screen and (min-width: @mobile-break-width) {
    padding-top: 12px;
    background: #f5f6fa;

    &.single-file {
      padding-bottom: 24px;
    }

    &-container {
      #public-page-content();
    }
  }

  &.need-passcode {
    background: url('@/assets/images/share/passcode-bg.jpeg') var(--no-repeat-center) / cover #e1f1fe;
  }
}
</style>

<style>
.need-passcode .share {
  background: transparent;
}
</style>
